import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Panel, Pill, inputClass, buttonClass } from "../components/Bits";
import { adminApi, apiError } from "../adminApi";
import { Tabs, when } from "./AdminPlanScenarios";
import { MODEL_VERSION, model, reason, screenToInputs, top3 } from "../../lib/planModel";

const th = "px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40";
const td = "px-3 py-2 text-sm text-cream/80";
const label = "block font-mono text-[10px] uppercase tracking-[0.2em] text-cream/45";
const STATUSES = ["new", "contacted", "consult_booked", "proposal", "won", "not_a_fit", "archived"];
const LABELS = {
  company: "Company name", state: "Where most employees are based", industry: "Industry",
  fte: "Full-time employees", enrolled: "On the health plan", coverage: "Offers coverage today",
  cost: "Cost per enrolled employee per month", renewal: "Expected renewal increase",
  priorities: "What matters most", risk: "Feeling about cost swings", health: "Team's health claims",
  timing: "Next renewal or start date",
};

export function AdminPlanLeads() {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ status: "", source: "" });

  useEffect(() => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    adminApi.get(`/crm/plan-builder/leads${qs ? `?${qs}` : ""}`)
      .then(({ data }) => setRows(data.leads)).catch((e) => toast.error(apiError(e)));
  }, [filters]);

  return (
    <div className="space-y-6" data-testid="plan-leads">
      <h1 className="font-serif text-2xl font-medium text-cream">Group Plan Builder</h1>
      <Tabs active="Plan Check leads" />
      <div className="grid gap-4 sm:grid-cols-2">
        <select value={filters.status} className={inputClass} data-testid="pb-filter-status"
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.source} className={inputClass} data-testid="pb-filter-source"
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
          <option value="">All sources</option>
          {["qr", "email", "website", "direct", "other"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <Panel title="Plan Check leads" testid="pb-leads-panel">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead className="border-b border-cream/10"><tr>
              {["Received", "Company", "Contact", "Email", "FTEs", "Source", "Top option", "Timing", "Status"].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-cream/5">
              {(rows || []).map((l) => (
                <tr key={l.id} data-testid={`pb-lead-${l.id}`}>
                  <td className={td}>{when(l.created_at)}</td>
                  <td className={td}>
                    <Link to={`/admin/plan-builder/leads/${l.id}`} className="hover:text-gold">{l.company}</Link>
                    {l.status === "new" ? <span className="ml-2 border border-gold/50 px-1.5 py-0.5 font-mono text-[9px] uppercase text-gold">New</span> : null}
                  </td>
                  <td className={td}>{l.contact?.name}</td>
                  <td className={td}>{l.contact?.email}</td>
                  <td className={td}>{l.answers?.fte}</td>
                  <td className={td}><Pill tone="neutral">{l.attribution?.source}</Pill></td>
                  <td className={td}>{(l.result?.top || [])[0]?.title || "—"}</td>
                  <td className={td}>{l.answers?.timing}</td>
                  <td className={td}>{l.status}</td>
                </tr>
              ))}
              {rows && !rows.length ? (
                <tr><td className={td} colSpan={9} data-testid="pb-leads-empty">No Plan Check leads yet.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

export function AdminPlanLeadDetail() {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    adminApi.get(`/crm/plan-builder/leads/${leadId}`).then(({ data }) => {
      setLead(data);
      setNotes(data.notes || "");
    }).catch((e) => toast.error(apiError(e)));
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  if (!lead) return <p className="text-sm text-cream/50" data-testid="pb-lead-loading">Loading…</p>;

  const rescored = top3(model(screenToInputs(lead.answers)));
  const inputs = screenToInputs(lead.answers);

  const patch = async (changes) => {
    try {
      const { data } = await adminApi.patch(`/crm/plan-builder/leads/${leadId}`, changes);
      setLead(data);
      toast.success("Lead updated.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toPipeline = async () => {
    try {
      await adminApi.post(`/crm/plan-builder/leads/${leadId}/pipeline`);
      toast.success("Added to the pipeline.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="plan-lead-detail">
      <Tabs active="Plan Check leads" />
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto font-serif text-2xl font-medium text-cream">{lead.company}</h1>
        <Link className={buttonClass} data-testid="pb-open-builder"
          to={`/admin/plan-builder?lead=${lead.id}`}>Open in Group Plan Builder</Link>
        <button className={buttonClass} onClick={toPipeline} data-testid="pb-add-pipeline">Add to Pipeline</button>
      </div>
      {lead.model_version !== MODEL_VERSION ? (
        <p className="border border-gold/40 bg-gold/10 p-3 text-sm text-gold" data-testid="pb-lead-version">
          Scored with model {lead.model_version}; current model {MODEL_VERSION}
        </p>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Contact" testid="pb-lead-contact">
          <p className="text-sm text-cream/80">{lead.contact.name}{lead.contact.title ? ` · ${lead.contact.title}` : ""}</p>
          <p className="mt-2 text-sm"><a href={`mailto:${lead.contact.email}`} className="text-gold">{lead.contact.email}</a></p>
          {lead.contact.phone ? <p className="mt-1 text-sm"><a href={`tel:${lead.contact.phone}`} className="text-gold">{lead.contact.phone}</a></p> : null}
          <p className="mt-4 text-xs leading-relaxed text-cream/45">Received {when(lead.created_at)}</p>
        </Panel>
        <Panel title="Attribution" testid="pb-lead-attribution">
          <dl className="space-y-1 text-sm text-cream/70">
            {Object.entries(lead.attribution || {}).map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-cream/35">{key}</dt>
                <dd>{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </Panel>
        <Panel title="Consent" testid="pb-lead-consent">
          <p className="text-sm leading-relaxed text-cream/70">{lead.consent.text}</p>
          <p className="mt-3 text-sm text-cream/70">{lead.consent.marketing_text || "Marketing emails: not opted in."}</p>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-cream/35">Captured {when(lead.consent.at)}</p>
        </Panel>
        <Panel title="Answers" testid="pb-lead-answers">
          <dl className="space-y-2">
            {Object.entries(LABELS).map(([key, text]) => {
              const value = lead.answers[key];
              return (
                <div key={key}>
                  <dt className={label}>{text}</dt>
                  <dd className="text-sm text-cream/75">{Array.isArray(value) ? value.join(", ") : (value || "—")}</dd>
                </div>
              );
            })}
          </dl>
        </Panel>
      </div>

      <Panel title="Results as the prospect saw them" testid="pb-lead-results">
        <div className="divide-y divide-cream/10">
          {(lead.result?.top || []).map((o) => (
            <div key={o.id} className="py-4">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-serif text-lg text-cream">{o.rank}. {o.title}</h3>
                <Pill tone={o.elig === "Eligible" ? "good" : "warn"}>{o.elig}</Pill>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-cream/35">Fit {o.fit}</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-cream/70">{o.line}</p>
              {o.why ? <p className="mt-1 text-sm text-cream/50">{o.why}</p> : null}
            </div>
          ))}
        </div>
        {(lead.result?.notes || []).length ? (
          <ul className="mt-4 space-y-2 border-l-2 border-gold pl-4">
            {lead.result.notes.map((n) => <li key={n} className="text-sm text-cream/60">{n}</li>)}
          </ul>
        ) : null}
      </Panel>

      <Panel title={`Re-scored with the current model (${MODEL_VERSION})`} testid="pb-lead-rescored">
        <ol className="space-y-3">
          {rescored.map((r) => (
            <li key={r.a.id} className="text-sm text-cream/70">
              <span className="text-cream">{r.a.name}</span> · Fit {Math.round(r.fit)} · {r.elig}
              <p className="mt-1 text-cream/50">{reason(r, inputs.risk, model(inputs).tol, inputs)}</p>
            </li>
          ))}
        </ol>
      </Panel>

      <Panel title="Status and notes" testid="pb-lead-status">
        <select value={lead.status} className={inputClass} data-testid="pb-lead-status-select"
          onChange={(e) => patch({ status: e.target.value })}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <textarea rows={4} value={notes} className={`${inputClass} mt-4`} data-testid="pb-lead-notes"
          onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes" />
        <button className={`${buttonClass} mt-4`} onClick={() => patch({ notes })} data-testid="pb-lead-save-notes">Save notes</button>
      </Panel>
    </div>
  );
}
